// Phase 2 retention content: rewards, Supply Drops, Daily Supply, Directives, Milestones, Salvage Shop.
// All tuning lives here; rules are in src/game/meta.ts.
import type { Rarity } from './equipment';

/** A bundle of rewards. Scrap is expressed in "kills" so it scales with the player's current stage. */
export interface Reward {
  cells?: number;          // Power Cells (premium currency)
  scrapKills?: number;     // Scrap worth this many normal kills at the current stage
  parts?: number;          // Drone Parts for a random drone
  energy?: 'full' | number;
  item?: Rarity;           // one Component of at least this rarity
}

// ---- Supply Drops ---------------------------------------------------------------------------

export const SUPPLY_DROP = {
  /** Seconds between drops (random in range). */
  minGapSec: 70,
  maxGapSec: 140,
  /** How long a drop stays on screen before leaving. */
  lifetimeSec: 12,
  /** Weighted reward table. */
  table: [
    { weight: 50, reward: { scrapKills: 40 } as Reward, label: 'Coin pouch' },
    // Energy is what gates Programs (the strongest multipliers), so drops only top it up a little:
    // a full refill here roughly doubled progression speed in the simulator.
    { weight: 12, reward: { energy: 40 } as Reward, label: 'Wind-up' },
    { weight: 18, reward: { parts: 2 } as Reward, label: 'Pal treats' },
    { weight: 8,  reward: { scrapKills: 150 } as Reward, label: 'Coin chest' },
    { weight: 4,  reward: { cells: 2 } as Reward, label: 'Gems' },
  ],
} as const;

// ---- Daily Supply (7-day login streak) ------------------------------------------------------

export const DAILY_SUPPLY: readonly Reward[] = [
  { scrapKills: 100 },
  { cells: 5 },
  { parts: 5 },
  { cells: 10, scrapKills: 200 },
  { item: 'epic' },
  { cells: 15, parts: 8 },
  { cells: 30, item: 'legendary' },
];

// ---- Directives (daily quests) --------------------------------------------------------------

export type DirectiveMetric =
  | 'kills' | 'bossKills' | 'taps' | 'casts' | 'botLevels' | 'stages' | 'reboots' | 'drops' | 'partsFed';

export interface DirectiveDef {
  id: string;
  metric: DirectiveMetric;
  target: number;
  /** `{n}` is replaced with the target. */
  text: string;
  reward: Reward;
}

export const DIRECTIVES_PER_DAY = 3;

export const DIRECTIVE_POOL: readonly DirectiveDef[] = [
  { id: 'kills500',   metric: 'kills',     target: 500,  text: 'Defeat {n} Gloom toys',         reward: { cells: 5, scrapKills: 60 } },
  { id: 'kills2000',  metric: 'kills',     target: 2000, text: 'Defeat {n} Gloom toys',         reward: { cells: 10 } },
  { id: 'boss25',     metric: 'bossKills', target: 25,   text: 'Topple {n} Giants',             reward: { cells: 6, scrapKills: 80 } },
  { id: 'boss100',    metric: 'bossKills', target: 100,  text: 'Topple {n} Giants',             reward: { cells: 10 } },
  { id: 'taps1000',   metric: 'taps',      target: 1000, text: 'Tap {n} times',             reward: { cells: 5, parts: 2 } },
  { id: 'casts10',    metric: 'casts',     target: 10,   text: 'Use {n} Specials',             reward: { cells: 5, energy: 40 } },
  { id: 'casts30',    metric: 'casts',     target: 30,   text: 'Use {n} Specials',             reward: { cells: 8 } },
  { id: 'levels200',  metric: 'botLevels', target: 200,  text: 'Level Guardians {n} times',      reward: { cells: 5, scrapKills: 80 } },
  { id: 'stages50',   metric: 'stages',    target: 50,   text: 'Clear {n} stages',             reward: { cells: 6 } },
  { id: 'reboot1',    metric: 'reboots',   target: 1,    text: 'Rewind once',      reward: { cells: 8 } },
  { id: 'drops3',     metric: 'drops',     target: 3,    text: 'Catch {n} Paper Plane Gifts',  reward: { cells: 5, parts: 3 } },
  { id: 'feed5',      metric: 'partsFed',  target: 5,    text: 'Feed Pals {n} times',     reward: { cells: 5 } },
];

/** Bonus for finishing all of today's directives. */
export const DIRECTIVES_BONUS: Reward = { cells: 10, item: 'rare' };

// ---- Milestones (achievements) --------------------------------------------------------------

export type MilestoneMetric =
  | 'bestStage' | 'kills' | 'bossKills' | 'taps' | 'reboots' | 'botsOwned' | 'techOwned' | 'drops' | 'directives';

export interface MilestoneDef {
  id: string;
  metric: MilestoneMetric;
  name: string;
  icon: string;
  /** `{n}` is replaced with the tier's target. */
  text: string;
  tiers: readonly number[];
}

/** Every claimed tier grants this much all damage (multiplicative per tier) plus Power Cells. */
export const MILESTONE_TUNING = { damagePerTier: 0.02, cellsBase: 5, cellsPerTier: 5 } as const;

export const MILESTONES: readonly MilestoneDef[] = [
  { id: 'stage',    metric: 'bestStage', name: 'Deep Push',      icon: '🗺️', text: 'Reach stage {n}',               tiers: [50, 100, 250, 500, 1000, 1500, 2000] },
  { id: 'kills',    metric: 'kills',     name: 'Toy Tamer',      icon: '💥', text: 'Defeat {n} Gloom toys',          tiers: [1000, 10000, 100000, 1000000] },
  { id: 'bosses',   metric: 'bossKills', name: 'Giant Toppler',  icon: '👑', text: 'Topple {n} Giants',          tiers: [100, 1000, 10000, 50000] },
  { id: 'taps',     metric: 'taps',      name: 'Tireless',       icon: '👆', text: 'Tap {n} times',              tiers: [1000, 10000, 100000, 1000000] },
  { id: 'reboots',  metric: 'reboots',   name: 'Night Owl',      icon: '🌙', text: 'Rewind {n} times',       tiers: [1, 5, 25, 100] },
  { id: 'bots',     metric: 'botsOwned', name: 'Big Brigade',    icon: '🧸', text: 'Recruit {n} different Guardians',     tiers: [10, 25, 40, 50] },
  { id: 'tech',     metric: 'techOwned', name: 'Collector',      icon: '🏆', text: 'Find {n} Treasures',      tiers: [5, 15, 28] },
  { id: 'drops',    metric: 'drops',     name: 'Air Mail',       icon: '✈️', text: 'Catch {n} Paper Plane Gifts',        tiers: [10, 100, 500] },
  { id: 'dailies',  metric: 'directives', name: 'Quest Master',  icon: '📋', text: 'Complete {n} Quests',       tiers: [10, 50, 200] },
];

// ---- Salvage Shop (spend Power Cells) --------------------------------------------------------

export type ShopEffect =
  | { kind: 'reward'; reward: Reward }
  | { kind: 'boost'; boost: BoostId; minutes: number };

export type BoostId = 'scrap2x' | 'damage2x';

export interface ShopItem { id: string; name: string; icon: string; desc: string; cost: number; effect: ShopEffect }

/**
 * Gems buy coins, and nothing else (owner, 2026-10-15). Each bundle costs exactly what a Gem pack
 * gives (src/data/gemPacks.ts), and pays coins worth `scrapKills` enemies at the player's current
 * stage, so every bundle stays worth having all game. Coins give diminishing returns (each 10x adds
 * roughly 8-10 stages, measured with the simulator), so the coins per Gem rise with the bundle:
 * scrapKills ~= 30 x gems^1.5. A bigger buy is always the better deal.
 */
const coinBundle = (id: string, name: string, cost: number, scrapKills: number): ShopItem =>
  ({ id, name, icon: '🪙', desc: `Coins worth ${scrapKills.toLocaleString('en-US')} enemies`, cost, effect: { kind: 'reward', reward: { scrapKills } } });

export const SHOP_ITEMS: readonly ShopItem[] = [
  coinBundle('coins10',     'Coin Pouch',          10,      1_000),
  coinBundle('coins50',     'Coin Purse',          50,      10_000),
  coinBundle('coins100',    'Coin Sack',           100,     30_000),
  coinBundle('coins550',    'Coin Bag',            550,     400_000),
  coinBundle('coins1200',   'Coin Chest',          1_200,   1_250_000),
  coinBundle('coins2500',   'Coin Crate',          2_500,   3_750_000),
  coinBundle('coins6500',   'Coin Barrel',         6_500,   15_000_000),
  coinBundle('coins14000',  'Coin Vault',          14_000,  50_000_000),
  coinBundle('coins37500',  'Coin Hoard',          37_500,  220_000_000),
  coinBundle('coins77500',  'Coin Mountain',       77_500,  650_000_000),
  coinBundle('coins160000', 'Coin Kingdom',        160_000, 1_900_000_000),
  coinBundle('coins330000', 'Royal Coin Treasury', 330_000, 5_700_000_000),
];
